import { Box } from '@mui/material';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { read } from 'xlsx';

import { FileUpload, FileWithPreview } from '../../../../lib/components';
import { FeedbackAlert } from '../../../../lib/components/atomic/feedback';
import { useStepper } from '../../partials/actionStepperPartial';
import { ArrowForwardIconSvg, DownloadArrowIconSvg } from '../../../../lib/icons';
import { useCreateImport } from '../hooks/useCreateImport';
import { observer } from 'mobx-react-lite';
import { ImportTypeEnum } from '@pxtra-client-lib-legacy/gen';
import { Button } from '../../../../lib/components/atomic/button';

export const FILE_INPUT_STYLES = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  height: '100%',
  '& .MuiAlert-root': {
    maxWidth: 'unset'
  },
  '& .MuiFormControl-root > .MuiBox-root > .MuiBox-root': {
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'center'
  },
  '& .MuiFormControl-root .MuiBox-root[role="presentation"]': {
    flexGrow: 1,
    backgroundColor: 'unset',
    borderWidth: '1.5px',
    borderColor: 'grey.900'
  },
  '& .MuiFormControl-root > p.MuiTypography-body2': {
    fontSize: '1rem',
    marginBottom: '1.5rem',
    color: 'primary.main'
  }
};

const templateFileIds: Record<ImportTypeEnum, string> = {
  employees: '1_1W9QaoGtxW3ZFhO8H_lMNgAu3NU6wMLiuyP2s_gHrM'
};

export const UploadFileStep: FC = observer(() => {
  const { t } = useTranslation();
  const { newImport } = useCreateImport();
  const { setNextDisabled, setNextButtonConfig } = useStepper();

  const handleUpload = (value: unknown) => {
    const files = value as Array<FileWithPreview>;

    if (files.length > 1) {
      throw new Error('maximum number of files is 1');
    }

    const file = files[0];
    newImport.file = file;
    const reader = new FileReader();
    const rABS = !!reader.readAsBinaryString;

    reader.onload = (e) => {
      const binaryString = e.target?.result;
      if (binaryString && typeof binaryString === 'string') {
        const rawData = window.btoa(binaryString);
        newImport.encoded_string = rawData;
        const workbook = read(rawData);
        const sheetsName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetsName];

        newImport.fileData = sheet;
      }
    };

    if (file instanceof Blob) {
      if (rABS) reader.readAsBinaryString(file);
      else reader.readAsArrayBuffer(file);
    }
  };

  useEffect(() => {
    setNextDisabled(!newImport.isFileUploaded);
    setNextButtonConfig({ text: t('common:NEXT'), endIcon: <ArrowForwardIconSvg /> });
  }, [newImport.isFileUploaded]);

  return (
    <Box sx={FILE_INPUT_STYLES}>
      <FileUpload
        tabIndex={1}
        name="file"
        label={t('form:SELECT_FILE_LABEL')}
        onChange={handleUpload}
        initFiles={newImport.file ? [newImport.file] : []}
        options={{
          maxFiles: 1,
          accept: {
            'application/vnd.ms-excel': ['.xls', '.xlsx']
          }
        }}
        error={!newImport.isFileUploaded}
        helperText={t('form:FIELD_IS_REQUIRED')}
      />
      <FeedbackAlert
        title={t('employee:DOWNLOAD_TEMPLATE_TITLE')}
        description={t('employee:DOWNLOAD_TEMPLATE_DESCRIPTION')}
        severity="info"
        actionElement={
          newImport.type ? (
            <a
              href={`https://docs.google.com/spreadsheets/d/${
                templateFileIds[newImport.type]
              }/export?format=xlsx`}
              className="my-auto no-underline"
            >
              <Button variant="secondary">
                <DownloadArrowIconSvg /> {t('common:DOWNLOAD')}
              </Button>
            </a>
          ) : (
            <></>
          )
        }
      />
    </Box>
  );
});
